// apps/web/src/pages/estoque/inventario-dialogs.tsx — Fase 13, Plano 29 (EST-13, D-24).
// Confirmações da revisão do inventário. "Aprovar ajustes" gera, numa só transação, todos os movimentos (sem aprovação
// parcial); o sucesso é a frase "Inventário ajustado: {N} movimentos gerados." (montada por textoSucessoAprovacao).
// Os diálogos ficam abertos quando a API devolve erro, com spinner e botões travados durante o envio (T-13-29C).
import { useEffect, useState } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { FormMessage } from '../../components/ui/form-message';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import type { ErroInventario } from '../../lib/estoque/inventario';
import { motivoDescarteValido, textoConfirmacaoAprovar, type ResumoRevisao } from '../../lib/estoque/inventario-revisao';

export interface AprovarAjustesDialogProps {
  readonly aberto: boolean;
  readonly resumo: ResumoRevisao;
  readonly enviando: boolean;
  readonly aoConfirmar: () => void;
  readonly aoFechar: () => void;
}

export function AprovarAjustesDialog({ aberto, resumo, enviando, aoConfirmar, aoFechar }: AprovarAjustesDialogProps) {
  return (
    <AlertDialog open={aberto} onOpenChange={(proximo) => !proximo && !enviando && aoFechar()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Aprovar ajustes</AlertDialogTitle>
          <AlertDialogDescription>{textoConfirmacaoAprovar(resumo)}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={enviando} className="min-h-11 sm:min-h-9">
            Voltar
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={enviando}
            className="min-h-11 sm:min-h-9"
            onClick={(event) => {
              event.preventDefault();
              aoConfirmar();
            }}
          >
            {enviando && <Loader2 className="animate-spin" aria-hidden="true" />}
            {enviando ? 'Aprovando...' : 'Aprovar ajustes'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export interface DescartarContagemDialogProps {
  readonly aberto: boolean;
  readonly enviando: boolean;
  /** Erro da API: o diálogo continua aberto e o motivo digitado é mantido. */
  readonly erro: ErroInventario | null;
  readonly aoConfirmar: (motivo: string) => void;
  readonly aoFechar: () => void;
  /** Conflito de versão: "Recarregar inventário" (nada local é descartado antes do clique). */
  readonly aoRecarregar: () => void;
}

/** Destrutivo (vermelho reservado pelo UI-SPEC): descarta a contagem inteira, sem gerar ajuste, com motivo obrigatório. */
export function DescartarContagemDialog({ aberto, enviando, erro, aoConfirmar, aoFechar, aoRecarregar }: DescartarContagemDialogProps) {
  const [motivo, setMotivo] = useState('');

  useEffect(() => {
    if (aberto) setMotivo('');
  }, [aberto]);

  const valido = motivoDescarteValido(motivo);

  return (
    <Dialog open={aberto} onOpenChange={(proximo) => !proximo && !enviando && aoFechar()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Descartar contagem</DialogTitle>
          <DialogDescription>A contagem será descartada e nenhum ajuste será gerado. O motivo fica registrado.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <Label htmlFor="descartar-motivo">Motivo (obrigatório)</Label>
          <Textarea
            id="descartar-motivo"
            rows={3}
            disabled={enviando}
            value={motivo}
            maxLength={500}
            aria-invalid={erro?.codigo === 'INVENTARIO_MOTIVO_OBRIGATORIO'}
            onChange={(e) => setMotivo(e.target.value)}
          />
        </div>
        {erro && (
          <div role="alert" className="flex flex-col gap-2">
            <FormMessage>{erro.mensagem}</FormMessage>
            {erro.conflitoDeVersao && (
              <Button type="button" variant="outline" className="min-h-11 self-start sm:min-h-9" onClick={aoRecarregar}>
                <RefreshCw aria-hidden="true" />
                Recarregar inventário
              </Button>
            )}
          </div>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" className="min-h-11 sm:min-h-9" disabled={enviando} onClick={aoFechar}>
            Voltar
          </Button>
          <Button type="button" variant="destructive" className="min-h-11 sm:min-h-9" disabled={enviando || !valido} onClick={() => aoConfirmar(motivo.trim())}>
            {enviando && <Loader2 className="animate-spin" aria-hidden="true" />}
            Descartar contagem
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
